import { getWebsocketBaseURL } from "../restapi/axiosClient";
import store from "../store";
import { AppNotification, pushNotify } from "../redux/notificationsSlice";
import { WireMessage, WSMessage } from "./wsTypes";
import { wsClosed, wsConnecting, wsIncoming, wsOpen } from "../redux/wsSlice";

import { upsertMessageFromWire } from "../db/gotd";
import { GetUserFullInfo } from "../utils/common";

export class WebSocketClient {
    private url: string;
    private reconnectInterval = 3000; // Giữ nguyên: thời gian reconnect
    private heartbeatInterval = 20000; // Giữ nguyên: khoảng cách gửi ping
    private reconnectTimer: any = null; // Thêm: để tránh reconnect loop
    private heartbeatTimer: any = null; // Giữ nguyên: heartbeat
    private messageQueue: string[] = []; // Giữ nguyên: hàng đợi tin nhắn
    private isManualClose = false; // Giữ nguyên: đóng tay sẽ không reconnect
    private logEnabled = true; // ✅ Thêm: bật/tắt log để debug

    public ws: WebSocket | null = null;
    public onMessageReceived?: (data: string) => void;
    public onMessageError?: (error: string) => void;
    public onConnect?: (msg: string) => void;
    public onCloseConnection?: (msg: string) => void;

    constructor(url: string) {
        this.url = url;
        setTimeout(() => this.connect(), 5000); // ✅ Sửa: delay nhẹ để tránh race khi store chưa sẵn sàng
    }

    private connect() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.isManualClose = false;
        this.url = getWebsocketBaseURL();
        if (this.logEnabled) console.log("🔌 Connecting WS:", this.url);
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
            if (this.logEnabled) console.log("🟢 WS connected:", this.url);
            this.onConnect?.("Đã kết nối");
            this.flushQueue();
            this.startHeartbeat();
            store.dispatch(wsOpen());
        };

        this.ws.onmessage = (msg: MessageEvent) => {
            const raw = msg.data;
            this.onMessageReceived?.(raw);

            let obj: WSMessage | null = null;
            try {
                obj = JSON.parse(String(raw));
            } catch {
                if (this.logEnabled) console.warn("⚠️ WS non-JSON message", raw);
                return;
            }

            if (!obj || obj.kind !== "event") return;

            if (
                obj.channel === "telegram" &&
                (obj.action === "tg_new" || obj.action === "new_message")
            ) {
                const data = obj.data?.message as WireMessage;
                if (!data) return;

                try {
                    const user = GetUserFullInfo();
                    data.message_id = obj.data.message.id;
                    upsertMessageFromWire(user.username, data);
                } catch (e) {
                    console.error("❌ Error upserting message:", e);
                }

                const sessionId = (obj as any).sessionId || (obj as any).session || "sess";
                const title = data.chat_title || "Tin nhắn mới";
                const preview = data.text?.trim() || "(Tin nhắn không có text)";

                const payload: AppNotification = {
                    id: `${sessionId}-${data.chat_id}-${data.message_id}-${Date.now()}`,
                    title: `${data.is_outgoing ? "Đã gửi" : "Tin mới"}: ${title}`,
                    message: preview,
                    level: "info",
                    createdAt: Date.now(),
                    sessionId,
                    chatId: data.chat_id,
                    messageId: data.message_id,
                };

                store.dispatch(pushNotify(payload));
                store.dispatch(wsIncoming(obj.data.message));

                if (this.logEnabled) {
                    console.log("📩 New WS msg:", {
                        chatId: data.chat_id,
                        msgId: data.message_id,
                        preview,
                    });
                }
            }
        };

        this.ws.onerror = (err: Event) => {
            console.error("❌ WS error", err);
            this.onMessageError?.("Lỗi WebSocket");
            store.dispatch({ type: "ws/wsError", payload: "Lỗi WebSocket" });
        };

        this.ws.onclose = () => {
            console.warn("🔴 WS closed");
            this.stopHeartbeat();
            this.onCloseConnection?.("Đã ngắt kết nối");
            store.dispatch(wsClosed());
            if (!this.isManualClose) {
                this.reconnect();
            }
        };
    }

    private reconnect() {
        if (this.reconnectTimer) return;
        store.dispatch(wsConnecting());
        console.log("⏳ Reconnecting in", this.reconnectInterval, "ms...");
        this.reconnectTimer = setTimeout(() => {
            this.url = getWebsocketBaseURL();
            this.connect();
        }, this.reconnectInterval);
    }

    private startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(
                    JSON.stringify({
                        kind: "event",
                        channel: "system",
                        action: "ping",
                        timestamp: Date.now(),
                        data: null,
                    }),
                );
            }
        }, this.heartbeatInterval);
    }

    private stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    private flushQueue() {
        while (this.ws?.readyState === WebSocket.OPEN && this.messageQueue.length > 0) {
            const msg = this.messageQueue.shift();
            if (msg) this.ws.send(msg);
        }
    }

    public sendMessage(msg: string) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(msg);
        } else {
            this.messageQueue.push(msg);
        }
    }

    public close() {
        this.isManualClose = true;
        this.stopHeartbeat();
        this.ws?.close();
    }

    public closeAndReconnect() {
        this.isManualClose = false;
        if (this.ws?.readyState === WebSocket.CONNECTING) {
            return; // Đang kết nối thì thôi
        }
        if(this.ws?.readyState === WebSocket.OPEN) {
            if (this.logEnabled) console.log("🔄 Reconnecting WS by closeAndReconnect");
        }
        if (this.ws?.readyState === WebSocket.CLOSING) {
            return; // Đang đóng thì thôi
        }
        if (this.ws?.readyState === WebSocket.CLOSED) {
            if (this.logEnabled) console.log("🔄 Reconnecting WS by closeAndReconnect (was closed)");
            this.connect();
            return;
        }
        this.ws?.close(); // sẽ trigger reconnect
    }

    public sendWireMessageViaWS(wsMessage: WSMessage): Promise<void> {
        return new Promise((resolve, reject) => {
            const json = JSON.stringify(wsMessage);
            if (this.logEnabled) console.log("📤 Send WS message:", json);
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(json);
                resolve();
            } else {
                reject("WebSocket not connected");
            }
        });
    }
}

// ===== Singleton an toàn với Vite/Webpack HMR =====
let wsInstance: WebSocketClient | null = null;

export function getWSClient(): WebSocketClient {
    if (!wsInstance) {
        const url = getWebsocketBaseURL();
        wsInstance = new WebSocketClient(url);
    }
    return wsInstance;
}

export const wsClientInstance = getWSClient();